import * as ISchema from "@schema";
import * as React from "react";
import { PostMessageManager } from "@util/post-message.util";
import {
  getRoomId,
  getRoomName,
  GetRoomNameWorkingGroup,
} from "@components/event-scheduler-suggested-times/EventSchedulerSuggestedTimes";
import {
  ConfirmableSuggestedTimeTileLarge,
  SuggestedTimeTimeLargePerson,
} from "@clockwise/web-commons/src/components/suggested-times-row/SuggestedTimesRow";
import { SuggestedRescheduleSlotsForEventQuery } from "./__generated__/SuggestedRescheduleSlotsForEvent.generated";
import { compact, isNumber } from "lodash";

type Org = Extract<SuggestedRescheduleSlotsForEventQuery["node"], { __typename: "Org" }>;
type ReschedulingResponse = Extract<
  Org["reschedulingResponseErrorable"],
  { __typename: "SchedulingResponse" }
>;
type EventSlotSlot = ReschedulingResponse["slots"][number];

const EventSlot = ({
  slot,
  index,
  workingGroup,
  people,
  onConfirm,
  isSelected,
}: {
  slot: EventSlotSlot | undefined;
  index: number;
  workingGroup: GetRoomNameWorkingGroup | any | null;
  people: SuggestedTimeTimeLargePerson[];
  onConfirm: () => void;
  isSelected: boolean;
}) => {
  // SPECIAL TYPESCRIPT HACK
  // Typescript is having trouble inferring the types after the following conditions
  // if (!slot?.timeRange?.startTime?.dateOrDateTime) { return return <NullSpan /> }
  // Thus allow it to infer the correct type by using a series of simple conditionals
  // and by explicitly constructing `slotWithProperTypeInference`
  const NullSpan = () => <span key={index} />;
  if (!slot) {
    return <NullSpan />;
  }
  const { timeRange, summary } = slot;
  if (!timeRange || !summary) {
    return <NullSpan />;
  }
  const { tags, focusTimeMinutesCost, unavailableAttendees } = summary;
  const { startTime } = timeRange;
  if (!startTime || !tags || !isNumber(focusTimeMinutesCost) || !unavailableAttendees) {
    return <NullSpan />;
  }
  const { dateOrDateTime } = startTime;
  if (!dateOrDateTime) {
    return <NullSpan />;
  }
  const compactTags = compact(tags);
  const slotWithProperTypeInference = {
    ...slot,
    ...{
      timeRange: {
        ...timeRange,
        startTime: {
          ...slot?.timeRange?.startTime,
          dateOrDateTime,
        },
      },
    },
    ...{
      summary: {
        ...summary,
        tags: [...compactTags],
        focusTimeMinutesCost,
        unavailableAttendees,
      },
    },
  };
  // SPECIAL TYPESCRIPT HACK

  const roomId = getRoomId(slot);
  const roomName = getRoomName(workingGroup, roomId);

  return (
    <ConfirmableSuggestedTimeTileLarge
      styleOverrides={{ margin: 0, cursor: isSelected ? "unset" : "pointer" }}
      key={index}
      slot={slotWithProperTypeInference}
      index={index}
      isLoading={false}
      isSelected={isSelected}
      roomName={roomName}
      onConfirm={onConfirm}
      onSelect={() => {}}
      people={people}
      onMouseEnter={(hoveredSlot: ISchema.ISchedulingSlot) => {
        const durationMinutes =
          (hoveredSlot.timeRange.endTime.millis - hoveredSlot.timeRange.startTime.millis) /
          (1000 * 60);
        PostMessageManager.sendShowEventOnCalendar(
          hoveredSlot.timeRange.startTime.millis,
          durationMinutes,
          true,
        );
      }}
      onMouseLeave={() => PostMessageManager.sendShowEventOnCalendar(null, null, false)}
    />
  );
};

export default EventSlot;
